import type { CalculationMode, CalculationResult, WeldParameterInputs } from "./types";

const PI_APPROX = 3.14;
const A_MASS_SAFETY_FACTOR = 0.9028;

export function wireAreaFromDiameter(wireDiameterMm: number) {
  return (wireDiameterMm * wireDiameterMm * PI_APPROX) / 4;
}

export function seamAreaFromAMass(aMassMm: number) {
  const theoreticalAMass = aMassMm / A_MASS_SAFETY_FACTOR;
  return theoreticalAMass * theoreticalAMass;
}

export function calculateAMass(inputs: WeldParameterInputs) {
  const wireArea = wireAreaFromDiameter(inputs.wireDiameter);
  const wireFeedMmMin = inputs.wireFeed * 1000;
  const travelSpeedMmMin = inputs.travelSpeed * 10;
  return Math.sqrt((wireFeedMmMin * wireArea) / travelSpeedMmMin);
}

export function calculatePredictedAMass(inputs: WeldParameterInputs) {
  return calculateAMass(inputs) * A_MASS_SAFETY_FACTOR;
}

export function calculateTravelSpeed(inputs: WeldParameterInputs) {
  const seamArea = seamAreaFromAMass(inputs.aMass);
  const wireArea = wireAreaFromDiameter(inputs.wireDiameter);
  const wireFeedMmMin = inputs.wireFeed * 1000;
  return (wireFeedMmMin * wireArea) / seamArea / 10;
}

export function calculateWireFeed(inputs: WeldParameterInputs) {
  const seamArea = seamAreaFromAMass(inputs.aMass);
  const wireArea = wireAreaFromDiameter(inputs.wireDiameter);
  const travelSpeedMmMin = inputs.travelSpeed * 10;
  return (travelSpeedMmMin * seamArea) / wireArea / 1000;
}

export function calculateWireDiameter(inputs: WeldParameterInputs) {
  const seamArea = seamAreaFromAMass(inputs.aMass);
  const wireFeedMmMin = inputs.wireFeed * 1000;
  const travelSpeedMmMin = inputs.travelSpeed * 10;
  const requiredWireArea = (travelSpeedMmMin * seamArea) / wireFeedMmMin;
  return Math.sqrt((requiredWireArea * 4) / PI_APPROX);
}

export function calculateWeldParameter(
  mode: CalculationMode,
  inputs: WeldParameterInputs
): CalculationResult {
  const wireArea = wireAreaFromDiameter(inputs.wireDiameter);
  const seamArea = seamAreaFromAMass(inputs.aMass);
  const wireFeedMmMin = inputs.wireFeed * 1000;
  const travelSpeedMmMin = inputs.travelSpeed * 10;
  const wireVolumeRate = wireArea * wireFeedMmMin;
  const seamVolumeRate = seamArea * travelSpeedMmMin;

  if (mode === "aMass") {
    return {
      mode,
      value: calculatePredictedAMass(inputs),
      metric: "aMass",
      label: "predictedAMass",
      formula: "sqrt((vD * wire area) / travel speed) * 0.9028",
      details: [
        { label: "rawAMass", value: calculateAMass(inputs), metric: "aMass" },
        { label: "safetyFactor", value: A_MASS_SAFETY_FACTOR, metric: "factor" },
        { label: "wireArea", value: wireArea, metric: "area" }
      ]
    };
  }

  if (mode === "travelSpeed") {
    return {
      mode,
      value: calculateTravelSpeed(inputs),
      metric: "travelSpeed",
      label: "travelSpeed",
      formula: "(vD * wire area / throat area)",
      details: [
        { label: "seamArea", value: seamArea, metric: "area" },
        { label: "wireArea", value: wireArea, metric: "area" },
        { label: "volumeRate", value: wireVolumeRate, metric: "volumeRate" }
      ]
    };
  }

  if (mode === "wireFeed") {
    return {
      mode,
      value: calculateWireFeed(inputs),
      metric: "wireFeed",
      label: "wireFeed",
      formula: "(travel speed * throat area / wire area)",
      details: [
        { label: "seamArea", value: seamArea, metric: "area" },
        { label: "wireArea", value: wireArea, metric: "area" },
        { label: "volumeRate", value: seamVolumeRate, metric: "volumeRate" }
      ]
    };
  }

  return {
    mode,
    value: calculateWireDiameter(inputs),
    metric: "wireDiameter",
    label: "wireDiameter",
    formula: "sqrt((required wire area * 4) / 3.14)",
    details: [
      { label: "seamArea", value: seamArea, metric: "area" },
      { label: "wireArea", value: seamVolumeRate / wireFeedMmMin, metric: "area" },
      { label: "volumeRate", value: seamVolumeRate, metric: "volumeRate" }
    ]
  };
}
